import { useCallback, useEffect, useRef } from 'react';
import { Button, Tag, notification } from 'antd';
import { useModel } from '@@/exports';
import { history } from '@@/exports';

type NotificationSocketConfig = {
  wsSuffix: string;
  handlePayload: (payload: string) => void;
};

const HEARTBEAT_INTERVAL = 30000;
const RECONNECT_DELAY = 3000;

const useNotificationSocket = ({ wsSuffix, handlePayload }: NotificationSocketConfig) => {
  const { initialState } = useModel('@@initialState');
  const heartbeatTimer = useRef<NodeJS.Timeout>();
  const reconnectTimer = useRef<NodeJS.Timeout>();
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    const username = initialState?.currentUser?.userName;
    if (!username || !wsSuffix || !handlePayload) {
      return;
    }

    let stopped = false;

    const cleanupHeartbeat = () => {
      if (heartbeatTimer.current) {
        clearInterval(heartbeatTimer.current);
        heartbeatTimer.current = undefined;
      }
    };

    const cleanupReconnect = () => {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = undefined;
      }
    };

    const connect = () => {
      if (stopped) {
        return;
      }
      const wsUrl = `${WS_URL}${username}${wsSuffix}`;
      const socket = new WebSocket(wsUrl);
      wsRef.current = socket;

      const startHeartbeat = () => {
        cleanupHeartbeat();
        heartbeatTimer.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('heartbeat');
          }
        }, HEARTBEAT_INTERVAL);
      };

      socket.onopen = () => {
        console.log(`Notification WebSocket connected: ${wsSuffix}`);
        startHeartbeat();
      };

      socket.onmessage = (event) => {
        if (event.data === 'heartbeat') return;
        handlePayload(event.data);
      };

      socket.onclose = (event) => {
        console.log('Notification WebSocket disconnected', event);
        cleanupHeartbeat();
        if (!stopped) {
          cleanupReconnect();
          reconnectTimer.current = setTimeout(() => {
            if (!stopped) {
              connect();
            }
          }, RECONNECT_DELAY);
        }
      };

      socket.onerror = (error) => {
        console.error('Notification WebSocket error:', error);
      };
    };

    connect();

    return () => {
      stopped = true;
      cleanupHeartbeat();
      cleanupReconnect();
      wsRef.current?.close();
    };
  }, [handlePayload, initialState?.currentUser?.userName, wsSuffix]);
};

const useProductAcceptanceNotification = () => {
  const handleProductAcceptanceMessage = useCallback((payload: string) => {
    const issueIds = typeof payload === 'string' ? payload.trim() : '';
    if (!issueIds) {
      return;
    }

    const issueIdList = issueIds
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    if (!issueIdList.length) {
      return;
    }

    notification.info({
      message: '产品验收通知',
      style: { width: 280 },
      description: (
        <div>
          <div>
            您有
            <Tag color="blue" style={{ margin: 6 }}>
              {issueIdList.length}
            </Tag>
            个需求待验收
          </div>
          <div style={{ fontSize: 11, color: '#808080' }}>关闭此通知后，您可以在通知中心查看</div>
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => {
              history.push('/rd/productIssue', { issueIds });
              notification.destroy();
            }}
          >
            去填写
          </Button>
        </div>
      ),
      placement: 'topRight',
      duration: null,
    });
  }, []);

  useNotificationSocket({
    wsSuffix: '_notification',
    handlePayload: handleProductAcceptanceMessage,
  });
};

const useSystemVersionNotification = () => {
  const handleSystemVersionMessage = useCallback((payload: string) => {
    const versionNo = typeof payload === 'string' ? payload.trim() : '';
    if (!versionNo) {
      return;
    }

    notification.info({
      message: '研发效能平台版本更新',
      style: { width: 280 },
      description: (
        <div>
          <div style={{ fontSize: 14, fontWeight: 'bold' }}>
            发布版本号：
            <Tag color="processing">{versionNo}</Tag>
          </div>
          <div style={{ fontSize: 11, color: '#808080' }}>关闭此通知后，您可以在通知中心查看</div>
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => {
              history.push('/settings/systemVersion');
              notification.destroy();
            }}
          >
            去查看
          </Button>
        </div>
      ),
      placement: 'topRight',
      duration: null,
    });
  }, []);

  useNotificationSocket({
    wsSuffix: '_systemVersion',
    handlePayload: handleSystemVersionMessage,
  });
};

const Notifications = () => {
  useProductAcceptanceNotification();
  useSystemVersionNotification();
  return null;
};

export default Notifications;
